// Ask Your Records - chat across the organization's conversations, answers
// grounded in transcripts with citations. The prototype answers extractively;
// cloud deployments swap in the self-hosted LLM behind the same endpoint.
import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete, Box, Button, Card, CardContent, Chip, Stack, TextField, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import lpsService from '../services/lpsService';

export default function LpsChatPage() {
  const [recordings, setRecordings] = useState([]);
  const [scope, setScope] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);
  const bottom = useRef(null);

  useEffect(() => { lpsService.listRecordings().then(setRecordings).catch(() => {}); }, []);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread]);

  const ask = async () => {
    if (!q.trim() || busy) return;
    const question = q.trim();
    setThread((t) => [...t, { who: 'you', text: question }]);
    setQ(''); setBusy(true);
    try {
      const res = await lpsService.ask(question, scope || null);
      setThread((t) => [...t, { who: 'lps', text: res.answer, citations: res.citations, provider: res.provider }]);
    } catch {
      setThread((t) => [...t, { who: 'lps', text: 'Something went wrong — try again.' }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Ask Your Records</Typography>
          <Typography variant="body2" color="text.secondary">
            Answers are grounded in your transcripts and cite the source.
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Autocomplete size="small" sx={{ minWidth: 320 }}
          options={[{ id: '', title: 'All records', department: '' },
            ...recordings.filter((r) => r.status === 'done')]}
          getOptionLabel={(o) => o.title || ''}
          renderOption={(props, o) => (
            <li {...props} key={o.id || 'all'}>
              <Box>
                <Typography variant="body2" fontWeight={o.id ? 500 : 700}>{o.title}</Typography>
                {o.id && (
                  <Typography variant="caption" color="text.secondary">
                    {o.department}{o.subject_id ? ` · ${o.subject_id}` : ''}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={[{ id: '', title: 'All records' },
            ...recordings.filter((r) => r.status === 'done')].find((o) => o.id === scope) || null}
          onChange={(e, v) => setScope(v ? v.id : '')}
          renderInput={(params) => (
            <TextField {...params} label="Scope — type to search records" />
          )}
        />
      </Stack>

      <Card variant="outlined" sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
        <CardContent>
          {!thread.length && (
            <Typography color="text.secondary" align="center" sx={{ mt: 6 }}>
              Try: “what did we agree about the retention team?” · “when did the
              panic attack happen?” · “who authorized the payment?”
            </Typography>
          )}
          {thread.map((m, i) => (
            <Box key={i} sx={{ mb: 2, textAlign: m.who === 'you' ? 'right' : 'left' }}>
              <Card sx={{
                display: 'inline-block', px: 2, py: 1, maxWidth: '80%',
                bgcolor: m.who === 'you' ? 'primary.main' : 'grey.100',
                color: m.who === 'you' ? 'primary.contrastText' : 'text.primary',
              }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
              </Card>
              {!!m.citations?.length && (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} justifyContent="flex-start">
                  {m.provider === 'self-hosted' && (
                    <Chip size="small" color="success" variant="outlined" label="🔒 in-VPC model" />
                  )}
                  {m.citations.map((c, j) => (
                    <Chip key={j} size="small" variant="outlined"
                      label={`${c.speaker} @ ${Math.floor(c.start / 60)}:${String(Math.floor(c.start % 60)).padStart(2, '0')}`} />
                  ))}
                </Stack>
              )}
            </Box>
          ))}
          <div ref={bottom} />
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1}>
        <TextField fullWidth size="small" placeholder="Ask anything about your recorded conversations…"
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ask()} />
        <Button variant="contained" endIcon={<SendIcon />} disabled={busy} onClick={ask}>{busy ? 'Thinking…' : 'Ask'}</Button>
      </Stack>
    </Box>
  );
}
